import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { AsyncPipe, NgForOf, NgIf } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
    EActionColumn,
    TActionColumn,
} from '@app/modules/table/children/table-column-action/interfaces/table-column-action.enum';
import { TGroupCustomAction } from '@app/modules/table/children/table-column-action/interfaces/table-column-action.type';
import { IButton, InterfaceSettingsTable, InterfaceSettingsUserColumns } from '@app/modules/table/table.interface';
import { PopupContentComponent } from '@components/popup-content/popup-content.component';
import { LANG_DICTIONARY_TOKEN } from '@constants/diConstant';
import {
    FuiButtonComponent,
    FuiIconComponent,
    FuiSelectComponent,
    FuiSpinnerComponent,
    FuiTextInputComponent,
} from '@fui/fui-components';
import { defaultIdNameItemService, IIdNameItem, IStringNameItem } from '@interfaces/fui-components/select.interface';
import { IMainLang } from '@interfaces/languages/main-lang';
import { FuiDestroyService } from '@services/destroy.service';
import { BehaviorSubject } from 'rxjs';
import {
    disabledStringNameItemService,
    IFilterRun,
    IGroupRun,
    ISortRun,
    IStringNameItemDisabled,
    ISummarizeRun,
    ITranslations,
    IValueForm,
} from '../interfaces/table-column-action.interface';

/** Максимальное количество динамических групп полей */
const MAX_DYNAMIC_FIELD = 5;

/**
 * Компонент действий над колонкой таблицы.
 * Адаптирован под различные действия
 */
@Component({
    selector: 'app-table-column-action',
    templateUrl: './table-column-action.component.html',
    styleUrls: ['../styles/table-column-action.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        AsyncPipe,
        NgForOf,
        PopupContentComponent,
        CdkDropList,
        CdkDrag,
        NgIf,
        CdkDragHandle,
        FuiButtonComponent,
        FuiIconComponent,
        FuiSelectComponent,
        FuiSpinnerComponent,
        FuiTextInputComponent,
    ],
    providers: [FuiDestroyService],
})
export class TableColumnActionComponent implements OnInit, OnChanges, OnDestroy {
    readonly dictionary: IMainLang = inject(LANG_DICTIONARY_TOKEN);

    readonly #fb: FormBuilder = inject(FormBuilder);

    readonly disabledStringNameItemService = disabledStringNameItemService;

    readonly defaultIdNameItemService = defaultIdNameItemService;

    private timeoutId: ReturnType<typeof setTimeout>;

    /** Карта полей сортировки или поиска (определение типа поля и его опций) */
    readonly sortOrderOrSearchFieldMap = new Map();

    /** Заголовок окна */
    title = '';

    /** Карта позиций элементов динамической формы */
    order = new Map();

    /** Кнопки действий в футере */
    footerButtons$: BehaviorSubject<IButton[]> = new BehaviorSubject<IButton[]>([]);

    /** Динамическая форма */
    dynamicForm: FormArray;

    /** Массив общих переводов, которые различаются в зависимости от действия */
    translations: ITranslations;

    /** Опции колонок пользователя */
    userColumnNameOptions: IStringNameItemDisabled[] = [];

    /** Опции Сортировки */
    sortOptions: IStringNameItem[] = [
        {
            id: 'asc',
            name: this.dictionary.t_sort_asc,
        },
        {
            id: 'desc',
            name: this.dictionary.t_sort_desc,
        },
    ];

    /** Опции Да/Нет */
    booleanOptions: IIdNameItem[] = [
        {
            id: 1,
            name: this.dictionary.system_yes,
        },
        {
            id: 0,
            name: this.dictionary.system_no,
        },
    ];

    /** Данные выведенных в таблицу пользователя колонок */
    #userColumns: InterfaceSettingsUserColumns[] = [];

    /** Тип действия */
    @Input() actionType: TActionColumn;

    /** Данные выведенных в таблицу пользователя колонок */
    @Input()
    set userColumns(columns: InterfaceSettingsUserColumns[]) {
        // Здесь сохраняем только колонки: остальные Input могут ещё не быть установлены.
        // Форму создаёт ngOnChanges, когда Angular передал все входные значения.
        this.#userColumns = columns || [];
    }

    /** Стартовые значения для формы */
    @Input() startValuesForm: IValueForm[] = [];

    /**
     * Задание строки подсказки в заголовке
     * preloader - показывать иконку прелоадера
     * text - текст подсказки
     */
    @Input() statusTooltip: null | { preloader: boolean; text: string } = null;

    /** Активные группы таблицы: их поля недоступны для повторной сортировки. */
    @Input() userGroup?: InterfaceSettingsTable['userGroup'];

    /** Данные для запуска события */
    @Output() filterRun = new EventEmitter<IFilterRun[]>();

    @Output() sortRun = new EventEmitter<ISortRun[]>();

    @Output() sortReset = new EventEmitter<void>();

    @Output() groupRun = new EventEmitter<IGroupRun[]>();

    @Output() summarizeRun = new EventEmitter<ISummarizeRun[]>();

    @Output() groupCustomAction = new EventEmitter<TGroupCustomAction>();

    @ViewChild(PopupContentComponent) popupContentComponent: PopupContentComponent;

    // Получить контрол как FormGroup
    asFormGroup(control: AbstractControl): FormGroup {
        return control as FormGroup;
    }

    ngOnInit() {
        // ngOnChanges обычно уже создал форму; повторная инициализация здесь не требуется.
        if (!this.dynamicForm) {
            this.#initContent();
        }
    }

    ngOnDestroy(): void {
        // Отложенный поиск не должен отправлять события после уничтожения компонента.
        clearTimeout(this.timeoutId);
    }

    /** При изменении основных свойств нужно заново инициировать контент */
    ngOnChanges(changes: SimpleChanges) {
        if (changes.actionType || changes.userColumns || changes.startValuesForm) {
            this.#initContent();
        }
    }

    /** Инициализация контента */
    #initContent() {
        // При смене действия отменяем поиск, запланированный для предыдущей формы.
        clearTimeout(this.timeoutId);

        switch (this.actionType) {
            case EActionColumn.filter:
                this.translations = {
                    title: this.dictionary.t_filter_data,
                    userColumnNameLabel: this.dictionary.t_field_for_filtering,
                    sortOrderOrSearchValueLabel: this.dictionary.t_value,
                };
                break;
            case EActionColumn.summarize:
                this.translations = {
                    title: this.dictionary.t_summarize_data,
                    userColumnNameLabel: this.dictionary.t_field_summation,
                    sortOrderOrSearchValueLabel: this.dictionary.t_summation_method,
                };
                break;
            case EActionColumn.group:
                this.translations = {
                    title: this.dictionary.t_group_data,
                    userColumnNameLabel: this.dictionary.t_field_for_grouping,
                    sortOrderOrSearchValueLabel: this.dictionary.t_grouping_sorting,
                };
                break;
            case EActionColumn.sort:
                this.translations = {
                    title: this.dictionary.t_sort_data,
                    userColumnNameLabel: this.dictionary.t_sorting_field,
                    sortOrderOrSearchValueLabel: this.dictionary.t_sorting,
                };
                break;
            default:
                this.translations = {
                    title: '',
                    userColumnNameLabel: '',
                    sortOrderOrSearchValueLabel: '',
                };
                break;
        }

        this.#initForm();
        this.#initButtons();
    }

    /** Восстанавливает правила из входных значений или создаёт одну пустую строку формы. */
    #initForm() {
        this.dynamicForm = this.#fb.array([]);
        // Конфигурации полей прежней формы больше не используются.
        this.sortOrderOrSearchFieldMap.clear();
        if (this.startValuesForm?.length) {
            this.startValuesForm.forEach((item: IValueForm) => {
                this.dynamicForm.push(this.#createGroup(item));
            });
        } else {
            this.dynamicForm.push(this.#createGroup());
        }

        // Инициализация fieldMap для каждого поля
        this.dynamicForm.controls.forEach((control, index: number) => {
            this.#afterInitializationField(index);
        });
        this.#updateAvailableOptions();
    }

    /** Определяет доступность колонки в списке колонок действия */
    #includeUserColumn(item: IStringNameItemDisabled) {
        if (this.actionType === EActionColumn.group) {
            return item.type !== 'file' && item.type !== 'gallery' && item.type !== 'file_versions';
        }
        return true;
    }

    /** Обновление доступных опций колонок пользователя с учетом текущих выборов */
    #updateAvailableOptions(): void {
        // Получаем все выбранные значения из формы
        const selectedValues = this.dynamicForm.controls
            .map((control) => control.get('userColumnName')?.value)
            .filter((value) => value && value !== '');

        // Каждый раз берём полный список: колонки, исключённые для группировки, доступны в фильтре.
        this.userColumnNameOptions = this.#userColumns
            .map((column) => ({ id: column.name, name: column.title, type: column.type }))
            .filter((x) => this.#includeUserColumn(x))
            .map((option) => ({
                ...option,
                // Фильтровать можно по одному столбцу несколько раз.
                disabled: this.actionType !== EActionColumn.filter && selectedValues.includes(option.id),
            }));
    }

    /** Метод для создания новой группы полей, поля всегда два у всех действий */
    #createGroup(values: IValueForm | null = null): FormGroup {
        return this.#fb.group({
            userColumnName: [values?.userColumnName ?? '', Validators.required],
            sortOrderOrSearchValue: [values?.sortOrderOrSearchValue ?? '', Validators.required],
        });
    }

    /**
     * Инициализация кнопок
     */
    #initButtons(): void {
        const buttons: IButton[] = []; // Массив кнопок

        let disabledAdd = false;
        if (this.dynamicForm.controls.length === MAX_DYNAMIC_FIELD && this.actionType !== EActionColumn.filter) {
            disabledAdd = true;
        }

        let isGroupingActive = false;
        if (!this.startValuesForm?.length) {
            isGroupingActive = true;
        }

        /** Добавить поле */
        let button: IButton = {
            name: this.dictionary?.t_add_field,
            icon: 'plus',
            color: 'soft',
            click: 'add',
            disabled: disabledAdd,
        };
        buttons.push(button); // Добавляем кнопку в массив кнопок

        if (this.actionType === EActionColumn.group) {
            /** Свернуть */
            button = {
                name: this.dictionary?.t_collapse,
                icon: 'arrow-collapse',
                color: 'soft-outline',
                click: 'collapse',
                disabled: isGroupingActive,
            };
            buttons.push(button); // Добавляем кнопку в массив кнопок

            /** Развернуть */
            button = {
                name: this.dictionary?.t_expand,
                icon: 'arrow-expand',
                color: 'soft-outline',
                click: 'expand',
                disabled: isGroupingActive,
            };
            buttons.push(button); // Добавляем кнопку в массив кнопок
        }

        /** Сбросить */
        button = {
            name: this.dictionary?.t_reset,
            icon: 'close',
            color: 'soft-outline',
            click: 'reset',
        };
        buttons.push(button); // Добавляем кнопку в массив кнопок

        // Обновляем observable
        this.footerButtons$.next(buttons);
    }

    /**
     * Обработка клика по кнопке
     * @param clickName - имя события клика
     * @param groupIndex - индекс динамической группы полей
     */
    public clickButton(clickName: string, groupIndex = 0): void {
        switch (clickName) {
            /** Добавить поле */
            case 'add':
                this.addGroup();
                break;
            /** Свернуть */
            case 'collapse':
                this.groupCustomAction.emit('collapse');
                break;
            /** Развернуть */
            case 'expand':
                this.groupCustomAction.emit('expand');
                break;
            /** Сбросить */
            case 'reset':
                this.dynamicForm = this.#fb.array([this.#createGroup()]);
                this.#afterInitializationField(0);
                this.#updateButtonState(false, 'add');
                this.#updateButtonState(true, 'expand');
                this.#updateButtonState(true, 'collapse');
                this.initAction();
                this.#updateAvailableOptions();
                break;
            /** Удалить */
            case 'remove':
                if (
                    this.dynamicForm.controls.length === MAX_DYNAMIC_FIELD &&
                    this.actionType !== EActionColumn.filter
                ) {
                    this.#updateButtonState(false, 'add');
                }

                // Если удаляем пустой элемент, перезапрашивать данные не нужно
                if (
                    this.dynamicForm.at(groupIndex).value.userColumnName ||
                    this.dynamicForm.at(groupIndex).value.sortOrderOrSearchValue
                ) {
                    this.#removeGroup(groupIndex);
                    this.initAction();
                } else {
                    this.#removeGroup(groupIndex);
                }

                // Если удаляем единственный элемент - создаем пустой новый
                if (this.dynamicForm.controls.length === 0) {
                    this.dynamicForm = this.#fb.array([this.#createGroup()]);
                }
                this.#updateFieldMap();

                if (this.dynamicForm.controls.length === 1) {
                    if (this.dynamicForm.invalid) {
                        // Дезейблим кнопки, группировка не выполнена.
                        this.#updateButtonState(true, 'expand');
                        this.#updateButtonState(true, 'collapse');
                    }
                }

                break;
        }
    }

    /** Инициируем выполнение действия */
    initAction(actionType = null) {
        if (actionType) {
            this.actionType = actionType;
        }
        // Немедленное применение заменяет ранее запланированный поиск.
        clearTimeout(this.timeoutId);
        // Пустые строки не отправляем; ноль сохраняем как допустимое значение фильтра.
        const dynamicFormValue: IValueForm[] = this.dynamicForm
            .getRawValue()
            .filter(
                (item: IValueForm) =>
                    item.userColumnName && item.sortOrderOrSearchValue != null && item.sortOrderOrSearchValue !== '',
            );
        const orderRules = dynamicFormValue.flatMap<ISortRun>((item) =>
            item.sortOrderOrSearchValue === 'asc' || item.sortOrderOrSearchValue === 'desc'
                ? [{ userColumnName: item.userColumnName, sortDirection: item.sortOrderOrSearchValue }]
                : [],
        );
        switch (this.actionType) {
            // Фильтрация
            case EActionColumn.filter:
                const filterData: IFilterRun[] = dynamicFormValue.map((item) => ({
                    userColumnName: item.userColumnName,
                    searchValue: String(item.sortOrderOrSearchValue),
                }));
                this.filterRun.emit(filterData);
                break;

            // Группировка
            case EActionColumn.group:
                const groupData: IGroupRun[] = orderRules;

                this.#updateButtonState(!groupData.length, 'expand');
                this.#updateButtonState(!groupData.length, 'collapse');

                this.groupRun.emit(groupData);
                break;

            // Сортировка
            case EActionColumn.sort:
                const sortData: ISortRun[] = orderRules;
                if (sortData.length) {
                    this.sortRun.emit(sortData);
                } else {
                    this.sortReset.emit();
                    this.close();
                }
                break;

            // Суммирование
            case EActionColumn.summarize:
                break;
        }
    }

    /** Добавление новой группы в форму */
    addGroup(): void {
        const index: number = this.dynamicForm.controls.length;
        this.dynamicForm.push(this.#createGroup());

        if (index + 1 >= MAX_DYNAMIC_FIELD && this.actionType !== EActionColumn.filter) {
            this.#updateButtonState(true, 'add');
        }

        // Обновляем fieldMap новыми полями и устанавливаем опции второго поля
        this.#afterInitializationField(index);
    }

    #updateButtonState(disabled: boolean, clickName: string): void {
        const currentButtons = this.footerButtons$.getValue();
        const updatedButtons = currentButtons.map((button) =>
            button.click === clickName ? { ...button, disabled } : button,
        );
        this.footerButtons$.next(updatedButtons);
    }

    /** Удаление группы по индексу */
    #removeGroup(index: number): void {
        this.dynamicForm.removeAt(index);
        // Обновляем опции колонок пользователя после удаления
        this.#updateAvailableOptions();
    }

    /** Действия после добавления полей.
     *  Инициализируем fieldMap - для директивы динамических полей.
     *  Подписываемся на изменения полей
     *
     *  @param index - индекс группы в formArray
     */
    #afterInitializationField(index: number): void {
        const group = this.dynamicForm.at(index) as FormGroup;
        const userColumnNameValue = group.get('userColumnName').value;

        // Инициализация fieldMap
        this.#initializeFieldMap(index, userColumnNameValue);
    }

    /**
     * Определяет конфигурацию поля взависимости от типа действия и типа колонки
     */
    #getFieldConfig(userColumnNameValue: string): {
        type: 'select' | 'input';
        column_type: string;
        disabled?: boolean;
        options?: any[];
    } {
        const isGroupOrSort = this.actionType === EActionColumn.group || this.actionType === EActionColumn.sort;

        if (isGroupOrSort) {
            return {
                type: 'select',
                column_type: '',
                disabled: userColumnNameValue === '',
                options: [],
            };
        }

        // Для фильтрации
        const isBoolean =
            this.#userColumns.findIndex((item) => item.name === userColumnNameValue && item.type === 'boolean') !== -1;
        const isBooleanFilter = isBoolean && this.actionType === EActionColumn.filter;

        return {
            type: isBooleanFilter ? 'select' : 'input',
            column_type: isBooleanFilter ? 'boolean' : '',
        };
    }

    /**
     * Определяет debounceTime для поля фильтра
     */
    debounceAction() {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
        }
        this.timeoutId = setTimeout(() => this.initAction(), 2000);
    }

    /**
     * Нужны ли опции для поля (только для группировки и сортировки)
     */
    #needsOptions(): boolean {
        return this.actionType === EActionColumn.group || this.actionType === EActionColumn.sort;
    }

    /**
     * Инициализация fieldMap
     */
    #initializeFieldMap(index: number, userColumnNameValue: string): void {
        const fieldConfig = this.#getFieldConfig(userColumnNameValue);

        this.sortOrderOrSearchFieldMap.set(index, {
            ...fieldConfig,
            label: this.translations.sortOrderOrSearchValueLabel,
        });

        if (userColumnNameValue && this.#needsOptions()) {
            this.#setOptionsSecondField(index);
        }
    }

    /**
     * Обработчик изменения имени колонки
     */
    handleUserColumnNameChange(index: number, newValue: string): void {
        // Обновляем fieldMap
        const field = this.sortOrderOrSearchFieldMap.get(index);
        const newFieldConfig = this.#getFieldConfig(newValue);

        this.sortOrderOrSearchFieldMap.set(index, {
            ...field,
            ...newFieldConfig,
        });

        // Заполняем опции если нужно
        if (this.#needsOptions()) {
            this.#setOptionsSecondField(index);
        }

        // Обновляем значение поля
        this.#updateFieldValue(index);

        // Обновляем доступные опции колонок пользователя
        this.#updateAvailableOptions();
    }

    /**
     * Обновление значения поля при изменении колонки
     */
    #updateFieldValue(index: number): void {
        const group = this.dynamicForm.at(index) as FormGroup;

        if (this.#needsOptions()) {
            group.get('sortOrderOrSearchValue').setValue('asc');
            this.initAction();
        } else {
            group.get('sortOrderOrSearchValue').setValue(null);
            // Смена поля снимает прежний фильтр и отменяет отложенный поиск по его значению.
            this.initAction();
        }
    }

    /**
     * Установка опций для второго поля (для действий сортировки и группировки)
     *
     * @param index - индекс группы в FormArray
     */
    #setOptionsSecondField(index: number) {
        const field = this.sortOrderOrSearchFieldMap.get(index);
        this.sortOrderOrSearchFieldMap.set(index, {
            ...field,
            options: this.sortOptions,
        });
    }

    /** Изменение порядка записей */
    drop(event: CdkDragDrop<string[]>) {
        // Если положение элементов не изменилось
        if (event.previousIndex === event.currentIndex) {
            return;
        }
        moveItemInArray(this.dynamicForm.controls, event.previousIndex, event.currentIndex);
        moveItemInArray(this.dynamicForm.value, event.previousIndex, event.currentIndex);

        this.#updateFieldMap();

        if (this.dynamicForm.valid && this.actionType !== EActionColumn.filter) {
            this.initAction();
        }
    }

    /** Обновление fieldMap */
    #updateFieldMap(): void {
        this.sortOrderOrSearchFieldMap.clear();
        for (let index = 0; index < this.dynamicForm.controls.length; index++) {
            this.#afterInitializationField(index);
        }
    }

    /** Отобразить popupContent компонента */
    show(x: number, y: number) {
        this.popupContentComponent.show(x, y + 4);
    }

    /** Скрыть контент */
    close() {
        this.popupContentComponent.hide();
    }

    isRowDisabled(index: number): boolean {
        if (this.isGroupedBy(index)) {
            return true;
        }
        return false;
    }

    isGroupedBy(index: number): boolean {
        if (this.actionType === EActionColumn.sort) {
            const columnName = this.dynamicForm.at(index).value.userColumnName;
            if (this.userGroup && this.userGroup.length > 0) {
                const hasGroup = this.userGroup.findIndex((x) => x.name === columnName) !== -1;
                if (hasGroup) {
                    return true;
                }
            }
        }
        return false;
    }
}
